import { Suspense, useState } from "react";
import { administratorRoles } from "../config/roles";
import { Button } from "../elements/Button";
import { usePageState } from "../context/usePageState";
import { CancelButton } from "../elements/CancelButton";
import { useEntityPage } from "./useEntityPage";
import { submitsOnce } from "./submitsOnce";
import { SaveSplitButton } from "./SaveSplitButton";
import { useIsEditingProperties } from "../builder/propertiesEditing";
import { useFooterScope } from "./footerScope";

/**
 * Last-modified summary and the save actions. Stacks on mobile; from sm the
 * summary sits left of the buttons.
 */
export function EntityPageFooter() {
  const {
    definition,
    record,
    canEdit,
    isSaving,
    isDirty,
    savingAction,
    lastModifiedLabel,
    wizard,
    onSave,
    onCancel,
    onRefresh,
  } = useEntityPage();
  const { hasAnyRole } = usePageState();
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const editingProperties = useIsEditingProperties();
  const scope = useFooterScope();

  if (wizard) {
    return null;
  }

  const footerActions = (definition?.footerActions ?? []).filter(
    (action) =>
      hasAnyRole(action.roles ?? administratorRoles) &&
      (!action.visible || action.visible(record ?? {}))
  );
  const ActionModal = footerActions.find((action) => action.id === openActionId)?.modal;
  const offered = (definition?.saveMenu ?? []).filter(
    (option) => !option.visible || option.visible(record ?? {})
  );
  const scoped = scope ? offered.find((option) => option.scope === scope) : undefined;
  const scopedReason = scoped?.disabledReason?.(record ?? {});
  const saveOptions = offered.filter((option) => !option.scope && !option.separate);
  const separateOptions = offered.filter((option) => !option.scope && option.separate);

  return (
    <>
    <div className="entity-page__footer d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-sm-between gap-2 gap-sm-3 px-3 px-md-4">
      {lastModifiedLabel && (
        // Truncates rather than wraps: it gives way to the buttons now, and two lines of
        // it would make the bar tall for the same reason the squashed buttons did.
        <p
          className="entity-page__footer-meta text-muted mb-0 text-truncate min-w-0"
          title={lastModifiedLabel}
        >
          {lastModifiedLabel}
        </p>
      )}
      {/* Never squashed: a `.btn` wraps its text, so "Save and close" would break to
          three lines and take the whole bar's height with it. */}
      {!editingProperties && (
      <div className="d-flex justify-content-end gap-3 ms-sm-auto flex-shrink-0">
        {footerActions.map((action) => (
          <Button
            key={action.id}
            text={action.label}
            variant={action.variant}
            disabled={isSaving}
            className="rounded-2 fw-medium"
            onClick={() => setOpenActionId(action.id)}
          />
        ))}
        {!definition?.hideNavigation && !(definition?.hideCancelWhenReadOnly && !canEdit) && (
          <CancelButton
            disabled={isSaving}
            onClick={onCancel}
          />
        )}
        {canEdit && scoped && (
          <Button
            text={savingAction === `save:${scoped.id}` ? `${scoped.label}...` : scoped.label}
            title={scopedReason ?? scoped.description}
            disabled={isSaving || Boolean(scopedReason)}
            showSpinner={savingAction === `save:${scoped.id}`}
            className="entity-page__footer-btn--primary rounded-2 fw-medium"
            onClick={() => onSave(false, scoped)}
          />
        )}
        {canEdit && !scoped && (
          <>
            {!submitsOnce(definition) && saveOptions.length > 0 && (
              <SaveSplitButton
                options={saveOptions}
                disabled={isSaving}
                savingAction={savingAction}
                onSave={() => onSave(false)}
                onSelect={(option) => onSave(false, option)}
              />
            )}
            {!submitsOnce(definition) && saveOptions.length === 0 && (
              <Button
                text="Save"
                title={undefined}
                disabled={isSaving || !isDirty}
                showSpinner={savingAction === "save"}
                className="entity-page__footer-btn--primary rounded-2 fw-medium"
                onClick={() => onSave(false)}
              />
            )}
            {separateOptions.map((option) => {
              const reason = option.disabledReason?.(record ?? {});

              return (
                <Button
                  key={option.id}
                  text={savingAction === `save:${option.id}` ? `${option.label}...` : option.label}
                  title={reason ?? option.description}
                  disabled={isSaving || Boolean(reason)}
                  showSpinner={savingAction === `save:${option.id}`}
                  className="entity-page__footer-btn--primary rounded-2 fw-medium"
                  onClick={() => onSave(false, option)}
                />
              );
            })}
            <Button
              text="Save and close"
              title={undefined}
              disabled={isSaving || !isDirty}
              showSpinner={savingAction === "save-and-close"}
              className="entity-page__footer-btn--primary rounded-2 fw-medium"
              onClick={() => onSave(true)}
            />
          </>
        )}
      </div>
      )}
    </div>
    {ActionModal && (
      <Suspense fallback={null}>
        <ActionModal
          record={record ?? {}}
          onClose={() => setOpenActionId(null)}
          onDone={onRefresh}
        />
      </Suspense>
    )}
    </>
  );
}

export default EntityPageFooter;
